import type React from 'react';
import type { JSX } from 'react';
import ArticleGrid from 'components/Grid/ArticleGrid/ArticleGrid';
import type { Hub } from 'interfaces/Home';
import type { Latest } from 'interfaces/LatestPage';
import useBreakpoint from 'hooks/useBreakpoint';
import RawHtml from 'utils/miscUtils/rawHtml';
import type { PresentedByProps } from 'components/PresentedBy';
import { RightRailStickyAd } from 'components/Ad/AdSlot/Styles';
import type { Package } from 'interfaces/Taxonomy';
import type { MenuItemProps } from 'interfaces/navigation/Header';
import getCommonTestId from 'constants/testsIds/common';
import * as S from 'styles/rightRail';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import { Microsites, SectionTitles } from 'constants/constants';
import { SectionVariant } from 'interfaces/Section';
import AdSlotToggle from 'components/Ad/AdSlot/AdSlotToggle';
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';
import { PageType } from 'interfaces/content/articles/Post';
import ErrorBoundary from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import dynamic from 'next/dynamic';
import cx from 'classnames';
import ArticleCard from 'components/Globals/ArticleCard';
import HubHeader from 'components/HubHeader';
import Pagination from 'components/Pagination/Pagination';
import type { BreadcrumbsProps } from 'interfaces/navigation/Breadcrumbs';
import HUBPAGE_TEST_IDS from 'constants/testsIds/hubpage';
import Breadcrumbs from 'components/Breadcrumbs';
import Banner from 'components/Banner';
import * as SHub from './HubStyles';
import { getRowNumber } from 'utils/miscUtils/nativoHelpers';

export interface HubPageProps {
  data: Hub | Package | Latest;
  type: PageType.TAG | PageType.PACKAGES | PageType.LATEST;
  sidebar?: (JSX.Element | null)[];
  presentedBy?: PresentedByProps;
  hideAds?: boolean;
  infiniteScroll: {
    slug: string;
    taxonomy: string;
    numPostsPerPage: number;
  };
  isMicrosite?: boolean;
  subMenu?: MenuItemProps[];
  color: string;
  platform?: string;
  pageSize: number;
  pagination?: PagePagination;
}

const LatestArticles = dynamic(() => import('components/recommends/ArticleSection/LatestArticles'));
const NextCustomImage = dynamic(() => import('components/Image'));
const RightRailFlex = dynamic(() => import('components/Ad/AdSlot/RightRailFlex'));

const HubPage: React.FC<HubPageProps> = ({
  data,
  pagination,
  type,
  sidebar,
  presentedBy,
  hideAds = false,
  isMicrosite = false,
  subMenu,
  platform,
  pageSize,
}) => {
  const hubPostsBuffer = [...data.posts];
  const { pageType } = data;
  const isTheLatest = pageType === 'latest';

  const isDesktop = useBreakpoint('hybridMin');
  const isMobile = useBreakpoint('mdMax');

  const isKKRSpecialIssue = data.slug === 'digital-issue-kkr';
  const kkrSpecialIssue = {
    dek: "Business news doesn't take a summer break. In the past six weeks our award-winning editorial staff has written must-read stories about private equity, crypto iniquity, misbehaving poultry royalty, and much more.",
    header: 'A Wall Street legend gets a radical makeover, and more from Fortune',
    image: {
      alt: "KKR's $1 Trillion Gamble",
      src: 'https://fortune.com/img-assets/wp-content/uploads/2024/08/11d2bafb-7c9f-4bd6-8522-433354030575.jpeg',
    },
    pageTitle: 'Special Digital Issue',
  };

  const dataCy = isDesktop ? 'sectionRightRail' : '';

  const hubPosts = hubPostsBuffer.slice(0, hubPostsBuffer.length);
  const pageDescription: string = Array.isArray(data.description) ? (data.description[0] as string) : data.description;
  const breadcrumbsData = (data as Hub).breadcrumbs?.items.length
    ? [{ label: 'home', link: '/' }, ...((data as Hub).breadcrumbs as BreadcrumbsProps).items]
    : [
        { label: 'home', link: '/' },
        { label: data.name, link: data.link },
      ];

  return (
    <SHub.HubWrapper className={type}>
      <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
        <Breadcrumbs
          isHubPage
          items={breadcrumbsData}
        />
      </ErrorBoundary>
      <SHub.Content>
        <SHub.BodyWrapper>
          {type === PageType.LATEST && (
            <SHub.ArticleWrapper className={type}>
              <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                <SHub.Header className={cx({ 'the-latest': isTheLatest })}>
                  <HubHeader
                    pageType={pageType}
                    title={data.name}
                    subMenu={subMenu}
                    sponsor={
                      presentedBy
                        ? { ...presentedBy, link: presentedBy.linkTo || '', thumbnail: presentedBy.logo || '' }
                        : undefined
                    }
                  />
                </SHub.Header>
              </ErrorBoundary>
              <SHub.LatestWrapper>
                {pagination && pagination.showPageInfo && (
                  <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                    <SHub.PageInfo
                      className={cx({ 'the-latest': isTheLatest })}
                      as='h4'
                      $size={{ default: 'level7', mdMin: 'level6' }}
                      $fontFamily='graphikCond'
                    >
                      {pagination.pageInfo}
                    </SHub.PageInfo>
                  </ErrorBoundary>
                )}
                <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                  <LatestArticles
                    pagination={pagination}
                    title=''
                    variant={SectionVariant.LatestArticles}
                    articles={data.posts}
                    platform={Microsites.Own}
                    pinnedLinks={null}
                    trackingData={{
                      articleNameCD: `The Latest Homepage ${SectionTitles.latestArticles}`,
                      contentPlacementCD: `The Latest - Homepage - ${SectionTitles.latestArticles} grid - row`,
                    }}
                  />
                </ErrorBoundary>
              </SHub.LatestWrapper>
            </SHub.ArticleWrapper>
          )}

          {type !== PageType.LATEST && (
            <SHub.ArticleWrapper className={platform}>
              <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                <SHub.Header>
                  <HubHeader
                    title={data.name}
                    subMenu={subMenu}
                    sponsor={undefined}
                    pageInfo={pagination?.pageInfo || ''}
                  />
                </SHub.Header>
              </ErrorBoundary>
              {pageDescription && (
                <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                  <SHub.Excerpt
                    data-cy={getCommonTestId('DESCRIPTION')}
                    className='excerpt'
                  >
                    {RawHtml({ html: pageDescription })}
                  </SHub.Excerpt>
                </ErrorBoundary>
              )}
              {isKKRSpecialIssue && (
                <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                  <SHub.PackageSpecialIssue>
                    <NextCustomImage
                      alt={kkrSpecialIssue.image.alt}
                      src={kkrSpecialIssue.image.src}
                      width={320}
                      height={427}
                    />
                    <SHub.PackageSpecialText>
                      <SHub.PackageSpecialHead>
                        {kkrSpecialIssue.header.split(' ').map((word) =>
                          word === 'Fortune' ? (
                            <span key={word}>
                              <i>{word}</i>
                            </span>
                          ) : (
                            `${word} `
                          ),
                        )}
                      </SHub.PackageSpecialHead>
                      <SHub.PackageSpecialDek>{kkrSpecialIssue.dek}</SHub.PackageSpecialDek>
                    </SHub.PackageSpecialText>
                  </SHub.PackageSpecialIssue>
                </ErrorBoundary>
              )}
              <ErrorBoundary fragment={UIFragments.HUB_ARTICLE_BANNER}>
                {data.banner?.enabled && (
                  <SHub.BannerWrapper>
                    <Banner
                      enabled={data.banner.enabled}
                      desktop={data.banner.bannerDesktop}
                      mobile={data.banner.bannerMobile}
                      link={data.banner.link}
                    />
                  </SHub.BannerWrapper>
                )}
              </ErrorBoundary>
              <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
                <ArticleGrid
                  pageType={type}
                  pageSize={pageSize}
                  hideAds={hideAds}
                >
                  {hubPosts?.map((post: ArticlePreview, index: number) => (
                    <ArticleCard
                      key={post.databaseId}
                      dataCy={HUBPAGE_TEST_IDS.HUBPAGE_ARTICLE_CARD}
                      article={post}
                      layoutVariant='hero'
                      dataVariant={isMicrosite ? 'hub-microsite' : 'hub'}
                      eyebrowType='red'
                      titleSize={{ default: 'level3' }}
                      priority={isMobile && index === 0}
                      trackingData={{
                        contentPlacementCD: `${data.name} grid - row ${getRowNumber(index, isDesktop, isMobile) + 1}`,
                        eventAction: 'title click',
                        eventCategory: 'navigation',
                        eventLabel: post.title,
                      }}
                    />
                  ))}
                </ArticleGrid>
              </ErrorBoundary>
              <Pagination pagination={pagination} />
            </SHub.ArticleWrapper>
          )}

          <ErrorBoundary fragment={UIFragments.HUB_PAGE}>
            <S.RightRail data-cy={dataCy}>
              {!hideAds && (
                <RightRailFlex
                  id={0}
                  dataCy={getCommonTestId('RIGHT_RAIL_AD')}
                />
              )}
              {sidebar}
              {isDesktop && !hideAds && (
                <RightRailStickyAd data-cy={getCommonTestId('RIGHT_RAIL_STICKY_AD_HUB')}>
                  <AdSlotToggle
                    placementName='RightRailFlex'
                    sticky
                    index={3}
                    slotId={`RightRailFlex${3}`}
                  />
                </RightRailStickyAd>
              )}
            </S.RightRail>
          </ErrorBoundary>
        </SHub.BodyWrapper>
      </SHub.Content>
    </SHub.HubWrapper>
  );
};

export default HubPage;
